import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Link2, Copy, Users, DollarSign, Clock, CheckCircle2, LogOut, ExternalLink, Download, QrCode, FileText,
  ShoppingBag, UserPlus, AlertTriangle, Star, GraduationCap, BookOpen, Trophy, Target, Zap, Award
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { ClickAnalyticsCard } from '@/components/ClickAnalyticsCard';
import { PartnerImplantClientsTab } from '@/components/PartnerImplantClientsTab';
import { HelpButton } from '@/components/HelpButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SITE_URL = 'https://falocomodono.com.br';

// Inline form usado no card de alerta "Cadastre seu Pix"
const PixCadastroForm = ({ onSaved, getToken }) => {
  const [pixKey, setPixKey] = useState('');
  const [pixType, setPixType] = useState('CPF');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!pixKey.trim()) { toast.error('Informe a chave Pix'); return; }
    setSaving(true);
    try {
      const token = getToken();
      const res = await axios.put(`${API}/affiliates/me/pix`, { pix_key: pixKey.trim(), pix_type: pixType }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || 'Pix cadastrado!');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao cadastrar Pix');
    } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <select value={pixType} onChange={(e) => setPixType(e.target.value)} className="h-10 border border-amber-300 rounded-lg px-3 text-sm bg-white sm:w-32" data-testid="pix-type-select">
        <option value="CPF">CPF</option>
        <option value="CNPJ">CNPJ</option>
        <option value="EMAIL">Email</option>
        <option value="PHONE">Telefone</option>
        <option value="EVP">Aleatoria</option>
      </select>
      <input value={pixKey} onChange={(e) => setPixKey(e.target.value)} placeholder="Sua chave Pix" className="h-10 flex-1 border border-amber-300 rounded-lg px-3 text-sm bg-white" data-testid="pix-key-input" />
      <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white h-10 font-semibold shrink-0" data-testid="save-pix-btn">
        {saving ? '...' : 'Salvar Pix'}
      </Button>
    </div>
  );
};

const AffiliateDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vitrineGranting, setVitrineGranting] = useState(null);
  const [vitrineMonths, setVitrineMonths] = useState(3);

  const getToken = () => localStorage.getItem('affiliate_token');

  const fetchDashboard = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) { navigate('/afiliados/login'); return; }
      const headers = { Authorization: `Bearer ${token}` };
      const [dashRes, gamiRes, rankRes, campRes] = await Promise.all([
        axios.get(`${API}/affiliates/dashboard`, { headers }),
        axios.get(`${API}/affiliates/gamification`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/affiliates/ranking`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/campaigns/active`).catch(() => ({ data: { campaigns: [] } })),
      ]);
      setData(dashRes.data);
      setGamification(gamiRes.data);
      setRanking(rankRes.data);
      setActiveCampaigns(campRes.data?.campaigns || []);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('affiliate_token');
        localStorage.removeItem('affiliate_data');
        navigate('/afiliados/login');
      }
      toast.error('Erro ao carregar painel');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleLogout = () => {
    localStorage.removeItem('affiliate_token');
    localStorage.removeItem('affiliate_data');
    navigate('/afiliados/login');
  };

  const grantVitrine = async (companyId) => {
    try {
      const token = getToken();
      await axios.post(`${API}/affiliates/grant-vitrine`, {
        company_id: companyId,
        months: vitrineMonths
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cortesia Vitrine concedida com sucesso!');
      setVitrineGranting(null);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao conceder cortesia');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#C5A059] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { affiliate, referral_count, summary, commissions, referrals, stats, team, team_summary } = data;
  const isMaster = affiliate.affiliate_type === 'master';

  // Links
  const vendedorLink = `${SITE_URL}/vendedor/${affiliate.slug}`;
  const inviteLink = `${SITE_URL}/convite/${affiliate.referral_code || affiliate.slug}`;

  const copyVendedorLink = () => {
    navigator.clipboard.writeText(vendedorLink);
    toast.success('Link da Página de Vendas copiado!');
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success('Link de Recrutamento copiado!');
  };

  const shareWhatsAppVendedor = () => {
    const msg = encodeURIComponent(
      `Conheça o Falo Com O Dono! Um sistema que ajuda sua empresa a receber mais avaliações no Google e crescer de verdade.\n\nVeja os planos: ${vendedorLink}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const shareWhatsAppInvite = () => {
    const msg = encodeURIComponent(
      `Quer se tornar um consultor Falo Com O Dono? Cadastre-se aqui: ${inviteLink}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById('affiliate-qr-canvas');
    if (!canvas) return;
    const hiResCanvas = document.createElement('canvas');
    const size = 1024;
    hiResCanvas.width = size;
    hiResCanvas.height = size;
    const ctx = hiResCanvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(canvas, 0, 0, size, size);
    const link = document.createElement('a');
    link.download = `qrcode-afiliado-${affiliate.referral_code || 'fcd'}.png`;
    link.href = hiResCanvas.toDataURL('image/png');
    link.click();
  };

  const downloadStickerPDF = async () => {
    const canvas = document.getElementById('affiliate-qr-canvas');
    if (!canvas) return;
    toast.info('Gerando adesivo PDF...');
    try {
      const { jsPDF } = await import('jspdf');
      const W = 80, H = 80;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [W, H] });
      doc.setFillColor(10, 38, 71);
      doc.rect(0, 0, W, H, 'F');
      doc.setDrawColor(197, 160, 89);
      doc.setLineWidth(1.2);
      doc.roundedRect(2, 2, W - 4, H - 4, 3, 3, 'S');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(197, 160, 89);
      doc.text('FALO COM O DONO', W / 2, 10, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(5.5);
      doc.setTextColor(255, 255, 255);
      doc.text('Avalie, Sugira e Elogie', W / 2, 14.5, { align: 'center' });
      doc.text('diretamente com o Dono!', W / 2, 17.5, { align: 'center' });
      const qrHiRes = document.createElement('canvas');
      const qrSize = 800;
      qrHiRes.width = qrSize;
      qrHiRes.height = qrSize;
      const qrCtx = qrHiRes.getContext('2d');
      qrCtx.fillStyle = '#FFFFFF';
      qrCtx.fillRect(0, 0, qrSize, qrSize);
      qrCtx.imageSmoothingEnabled = false;
      qrCtx.drawImage(canvas, 0, 0, qrSize, qrSize);
      const qrDataUrl = qrHiRes.toDataURL('image/png');
      const qrBoxSize = 40;
      const qrX = (W - qrBoxSize) / 2;
      const qrY = 20;
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(qrX - 2, qrY - 2, qrBoxSize + 4, qrBoxSize + 4, 2, 2, 'F');
      doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrBoxSize, qrBoxSize);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6);
      doc.setTextColor(197, 160, 89);
      doc.text('Aponte a camera e cadastre-se!', W / 2, 66, { align: 'center' });
      const code = affiliate.referral_code || affiliate.slug || '';
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(4.5);
      doc.setTextColor(180, 180, 180);
      doc.text(`falocomodono.com.br/convite/${code}`, W / 2, 70, { align: 'center' });
      doc.setFontSize(4);
      doc.setTextColor(140, 140, 140);
      doc.text(`Consultor: ${affiliate.name || ''}`, W / 2, 74, { align: 'center' });
      doc.save(`adesivo-falocomodono-${code}.pdf`);
      toast.success('Adesivo PDF gerado com sucesso!');
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Erro ao gerar PDF. Tente novamente.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#0A2647] text-white px-3 sm:px-4 py-3 sm:py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-2">
          <div className="min-w-0">
            <Link to="/" className="text-[#C5A059] font-bold text-xs sm:text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>Falo Com O Dono</Link>
            <h1 className="text-base sm:text-xl font-bold mt-0.5 sm:mt-1">Painel do Afiliado</h1>
            <p className="text-xs sm:text-sm text-slate-400 truncate">
              {affiliate.name}
              {isMaster && <span className="ml-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#C5A059]/20 text-[#C5A059] border border-[#C5A059]/30">Master</span>}
            </p>
          </div>
          <Button variant="ghost" onClick={handleLogout} className="text-slate-400 hover:text-white shrink-0 px-2 sm:px-3" data-testid="aff-logout">
            <LogOut className="w-4 h-4" /><span className="ml-1.5 hidden sm:inline">Sair</span>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">

        {/* ══════ PAGINA DE VENDAS SEGMENTADA (Todos os afiliados) ══════ */}
        <Card className="border-[#C5A059]/30 shadow-md bg-gradient-to-br from-[#0A2647] to-[#0e3260]" data-testid="vendedor-link-card">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-[#C5A059] flex items-center justify-center shrink-0">
                <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5 text-[#0A2647]" />
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-white text-sm sm:text-lg">Sua Página de Vendas</h3>
                <p className="text-[10px] sm:text-xs text-white/50 truncate">Envie este link para quem já demonstrou interesse</p>
              </div>
            </div>
            <p className="text-xs sm:text-sm text-white/60 mb-3 sm:mb-4 leading-relaxed hidden sm:block">
              Esta página exibe todos os planos (Básico, Pro, Empresarial, Elite e 5 Estrelas) com copywriting otimizado para conversão.
              Ideal para enviar a donos de negócios que já demonstraram interesse.
            </p>
            <div className="flex items-center gap-2 mb-3 sm:mb-4">
              <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-lg px-2.5 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-mono text-[#C5A059] truncate border border-[#C5A059]/20" data-testid="vendedor-link-display">
                {vendedorLink}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={copyVendedorLink} className="bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] h-10 sm:h-11 flex-1 font-bold text-xs sm:text-sm" data-testid="vendedor-copy-btn">
                <Copy className="w-4 h-4 mr-1.5" />Copiar Link
              </Button>
              <Button onClick={shareWhatsAppVendedor} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 sm:h-11 flex-1 font-bold text-xs sm:text-sm" data-testid="vendedor-whatsapp-btn">
                <ExternalLink className="w-4 h-4 mr-1.5" />WhatsApp
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ══════ QR CODE DE VENDAS (Todos os afiliados) ══════ */}
        <Card className="border-[#C5A059]/20 shadow-sm" data-testid="vendedor-qrcode-card">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-4">
              <QrCode className="w-5 h-5 text-[#C5A059]" />
              <h3 className="font-bold text-slate-900 text-sm">QR Code de Vendas</h3>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-5">
              <div className="bg-white p-3 rounded-2xl border-2 border-[#C5A059]/20 shadow-inner" data-testid="vendedor-qrcode-container">
                <QRCodeCanvas
                  id="vendedor-qr-canvas"
                  value={vendedorLink}
                  size={180}
                  level="H"
                  marginSize={2}
                  fgColor="#0A2647"
                  bgColor="#FFFFFF"
                />
              </div>
              <div className="flex-1 text-center sm:text-left space-y-2.5">
                <p className="text-sm text-slate-600 leading-relaxed">
                  Imprima este QR Code e mostre para donos de negócios. Ao escanear, eles vao direto para sua pagina de vendas com planos de alto ticket.
                </p>
                <p className="text-xs text-slate-400 font-mono break-all">{vendedorLink}</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={() => {
                    const canvas = document.getElementById('vendedor-qr-canvas');
                    if (!canvas) return;
                    const hiRes = document.createElement('canvas');
                    hiRes.width = 1024; hiRes.height = 1024;
                    const ctx = hiRes.getContext('2d');
                    ctx.imageSmoothingEnabled = false;
                    ctx.drawImage(canvas, 0, 0, 1024, 1024);
                    const link = document.createElement('a');
                    link.download = `qrcode-vendas-${affiliate.slug}.png`;
                    link.href = hiRes.toDataURL('image/png');
                    link.click();
                    toast.success('QR Code de vendas baixado!');
                  }} className="bg-[#C5A059] hover:bg-[#b8933f] text-white h-10 px-4 gap-2 text-xs sm:text-sm" data-testid="vendedor-download-qr">
                    <Download className="w-4 h-4" />Baixar PNG (Alta Resolucao)
                  </Button>
                  <Button onClick={async () => {
                    const canvas = document.getElementById('vendedor-qr-canvas');
                    if (!canvas) return;
                    toast.info('Gerando adesivo PDF...');
                    try {
                      const { jsPDF } = await import('jspdf');
                      const W = 80, H = 80;
                      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [W, H] });
                      doc.setFillColor(10, 38, 71);
                      doc.rect(0, 0, W, H, 'F');
                      doc.setDrawColor(197, 160, 89);
                      doc.setLineWidth(1.2);
                      doc.roundedRect(2, 2, W - 4, H - 4, 3, 3, 'S');
                      doc.setFont('helvetica', 'bold');
                      doc.setFontSize(9);
                      doc.setTextColor(197, 160, 89);
                      doc.text('FALO COM O DONO', W / 2, 10, { align: 'center' });
                      doc.setFont('helvetica', 'normal');
                      doc.setFontSize(5.5);
                      doc.setTextColor(255, 255, 255);
                      doc.text('Conheca os planos e', W / 2, 14.5, { align: 'center' });
                      doc.text('leve para sua empresa!', W / 2, 17.5, { align: 'center' });
                      const qrHiRes = document.createElement('canvas');
                      qrHiRes.width = 800; qrHiRes.height = 800;
                      const qrCtx = qrHiRes.getContext('2d');
                      qrCtx.fillStyle = '#FFFFFF';
                      qrCtx.fillRect(0, 0, 800, 800);
                      qrCtx.imageSmoothingEnabled = false;
                      qrCtx.drawImage(canvas, 0, 0, 800, 800);
                      doc.setFillColor(255, 255, 255);
                      doc.roundedRect(18, 18, 44, 44, 2, 2, 'F');
                      doc.addImage(qrHiRes.toDataURL('image/png'), 'PNG', 20, 20, 40, 40);
                      doc.setFont('helvetica', 'bold');
                      doc.setFontSize(6);
                      doc.setTextColor(197, 160, 89);
                      doc.text('Aponte a camera e veja os planos!', W / 2, 66, { align: 'center' });
                      doc.setFont('helvetica', 'normal');
                      doc.setFontSize(4.5);
                      doc.setTextColor(180, 180, 180);
                      doc.text(`falocomodono.com.br/vendedor/${affiliate.slug}`, W / 2, 70, { align: 'center' });
                      doc.setFontSize(4);
                      doc.setTextColor(140, 140, 140);
                      doc.text(`Consultor: ${affiliate.name || ''}`, W / 2, 74, { align: 'center' });
                      doc.save(`adesivo-vendas-${affiliate.slug}.pdf`);
                      toast.success('Adesivo de vendas PDF gerado!');
                    } catch (err) {
                      toast.error('Erro ao gerar PDF');
                    }
                  }} className="bg-[#0A2647] hover:bg-[#0A2647]/90 text-white h-10 px-4 gap-2 text-xs sm:text-sm border border-[#C5A059]/30" data-testid="vendedor-download-sticker">
                    <FileText className="w-4 h-4" />Adesivo Pronto (PDF 8x8cm)
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ══════ RECRUTAMENTO — Apenas Master ══════ */}
        {isMaster && (
          <>
            <Card className="border-[#C5A059]/20 shadow-sm" data-testid="recrutamento-link-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <UserPlus className="w-5 h-5 text-[#C5A059]" />
                  <h3 className="font-bold text-slate-900">Link de Recrutamento</h3>
                  <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#C5A059]/10 text-[#C5A059] border border-[#C5A059]/30">Master</span>
                </div>
                <p className="text-xs text-slate-500 mb-3">Use este link para recrutar novos afiliados para sua rede.</p>
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 bg-slate-100 rounded-lg px-4 py-2.5 text-sm font-mono text-slate-700 truncate" data-testid="recrutamento-link-display">
                    {inviteLink}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={copyInviteLink} className="bg-[#0A2647] hover:bg-[#0A2647]/90 text-white h-10 flex-1 text-xs sm:text-sm" data-testid="recrutamento-copy-btn">
                    <Copy className="w-4 h-4 mr-1.5" />Copiar Link
                  </Button>
                  <Button onClick={shareWhatsAppInvite} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 flex-1 text-xs sm:text-sm" data-testid="recrutamento-whatsapp-btn">
                    <ExternalLink className="w-4 h-4 mr-1.5" />WhatsApp
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* QR Code Card — Master only */}
            <Card className="border-[#C5A059]/20 shadow-sm" data-testid="qrcode-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <QrCode className="w-5 h-5 text-[#C5A059]" />
                  <h3 className="font-bold text-slate-900">QR Code de Recrutamento</h3>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="bg-white p-4 rounded-2xl border-2 border-[#C5A059]/20 shadow-inner" data-testid="aff-qrcode-container">
                    <QRCodeCanvas
                      id="affiliate-qr-canvas"
                      value={inviteLink}
                      size={200}
                      level="H"
                      marginSize={2}
                      fgColor="#0A2647"
                      bgColor="#FFFFFF"
                    />
                  </div>
                  <div className="flex-1 text-center sm:text-left space-y-3">
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Imprima este QR Code em adesivos, cartoes ou materiais promocionais. Quando alguem escanear, sera direcionado para cadastrar-se como afiliado.
                    </p>
                    <p className="text-xs text-slate-400 font-mono break-all">{inviteLink}</p>
                    <Button onClick={downloadQRCode} className="bg-[#C5A059] hover:bg-[#b8933f] text-white h-11 px-6 gap-2" data-testid="aff-download-qr">
                      <Download className="w-4 h-4" />Baixar para Impressao (PNG Alta Resolucao)
                    </Button>
                    <Button onClick={downloadStickerPDF} className="bg-[#0A2647] hover:bg-[#0A2647]/90 text-white h-11 px-6 gap-2 border border-[#C5A059]/30" data-testid="aff-download-sticker-pdf">
                      <FileText className="w-4 h-4" />Baixar Adesivo Pronto (PDF 8x8cm)
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ══════ CAMPANHA ATIVA — BONUS EXTRA ══════ */}
        {activeCampaigns.length > 0 && (
          <Card className="border-0 shadow-md bg-gradient-to-r from-[#C5A059] via-[#d4b06a] to-[#C5A059] text-[#0A2647]" data-testid="active-campaigns-banner">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🎁</span>
                <h3 className="font-bold text-sm">Campanha ATIVA! Bonus Extra Disponivel</h3>
              </div>
              {activeCampaigns.map(c => {
                const end = new Date(c.ends_at);
                const daysLeft = Math.max(0, Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24)));
                return (
                  <div key={c.id} className="bg-white/30 backdrop-blur-sm rounded-xl p-3 mt-2" data-testid={`active-camp-${c.id}`}>
                    <p className="font-bold text-sm">{c.name}</p>
                    <p className="text-xs leading-snug mt-0.5">
                      +R$ <strong>{Number(c.bonus_per_sale).toFixed(2)}</strong> em cada venda que voce fizer.
                      {daysLeft > 0 && <span> Termina em <strong>{daysLeft} {daysLeft === 1 ? 'dia' : 'dias'}</strong>.</span>}
                    </p>
                    {c.min_sales > 0 && <p className="text-[11px] mt-1 opacity-80">A partir da {c.min_sales}ª venda no periodo.</p>}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* ══════ ALERTA: PIX NAO CADASTRADO ══════ */}
        {gamification && !gamification.has_pix_key && (
          <Card className="border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 shadow-md" data-testid="pix-missing-alert" id="pix-settings-section">
            <CardContent className="p-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-6 h-6 text-amber-700" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-amber-900 text-sm">Cadastre sua chave Pix para receber automaticamente</p>
                  <p className="text-xs text-amber-800 leading-snug mt-0.5">Suas comissoes cairao em <strong>ate 24h</strong> apos cada venda, sem intervencao manual.</p>
                </div>
              </div>
              <PixCadastroForm onSaved={fetchDashboard} getToken={getToken} />
            </CardContent>
          </Card>
        )}

        {/* ══════ META SEMANAL + BADGES ══════ */}
        {gamification && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="gamification-row">
            {/* META SEMANAL */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-5 h-5" />
                  <h3 className="font-bold text-sm">Sua Meta desta Semana</h3>
                </div>
                <div className="mb-2 flex items-baseline gap-2">
                  <span className="text-4xl font-extrabold">{gamification.week_sales}</span>
                  <span className="text-lg text-emerald-100">/ {gamification.weekly_goal} vendas</span>
                </div>
                <div className="h-2.5 bg-emerald-800/30 rounded-full overflow-hidden mb-3">
                  <div className="h-full bg-white transition-all duration-700"
                    style={{ width: `${gamification.weekly_goal_percent}%` }} />
                </div>
                <p className="text-xs text-emerald-100 leading-snug">
                  {gamification.weekly_goal_percent >= 100
                    ? `🎉 Meta batida! Continue vendendo, cada venda extra é bônus.`
                    : `Complete ${gamification.weekly_goal - gamification.week_sales} venda${gamification.weekly_goal - gamification.week_sales > 1 ? 's' : ''} essa semana e ganhe ${gamification.weekly_goal_reward_hint}.`}
                </p>
                <p className="text-[11px] text-emerald-100/80 mt-1.5 leading-snug">
                  Quanto maior o plano vendido, maior o ganho: 2× Básico = R$ 80 · 2× 5 Estrelas = R$ 299,80.
                </p>
                {gamification.days_since_last_sale !== null && gamification.days_since_last_sale > 7 && (
                  <p className="text-xs bg-red-400/20 text-red-100 px-3 py-2 rounded-lg mt-3">
                    ⚠️ Sua ultima venda foi ha {gamification.days_since_last_sale} dias. Acelere para nao perder o ritmo!
                  </p>
                )}
              </CardContent>
            </Card>

            {/* BADGES */}
            <Card className="border-0 shadow-sm bg-white">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Award className="w-5 h-5 text-[#C5A059]" />
                  <h3 className="font-bold text-sm text-slate-800">Suas Conquistas</h3>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {gamification.badges.map(b => (
                    <div key={b.id}
                      className={`text-center p-2 rounded-xl transition-all ${b.unlocked ? 'bg-gradient-to-br from-[#C5A059]/20 to-[#C5A059]/5 ring-1 ring-[#C5A059]/40' : 'bg-slate-50 opacity-50'}`}
                      data-testid={`badge-${b.id}`}>
                      <div className={`text-2xl ${b.unlocked ? '' : 'grayscale'}`}>{b.emoji}</div>
                      <p className={`text-[10px] font-bold mt-0.5 ${b.unlocked ? 'text-slate-800' : 'text-slate-500'}`}>{b.label}</p>
                      {!b.unlocked && b.progress > 0 && (
                        <p className="text-[9px] text-slate-400 mt-0.5">{b.progress}/{b.required}</p>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500 mt-3 text-center">
                  Total: <strong className="text-slate-800">{gamification.total_sales}</strong> vendas historicas
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ══════ RANKING MENSAL (TOP 10) ══════ */}
        {ranking && ranking.ranking && ranking.ranking.length > 0 && (
          <Card className="border-0 shadow-sm" data-testid="ranking-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#C5A059]" />
                  <CardTitle className="text-base">Top 10 do Mes</CardTitle>
                </div>
                {ranking.my_position && (
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${ranking.my_in_top_10 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} data-testid="my-ranking-position">
                    Voce esta em #{ranking.my_position}
                  </span>
                )}
              </div>
              <CardDescription className="text-xs">Ranking resetado todo dia 1 do mes. Foco total pra subir!</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {ranking.ranking.map(r => {
                  const podium = r.position === 1 ? '🥇' : r.position === 2 ? '🥈' : r.position === 3 ? '🥉' : null;
                  return (
                    <div key={r.position}
                      className={`flex items-center gap-3 p-2.5 rounded-xl transition-all ${r.is_me ? 'bg-gradient-to-r from-[#C5A059]/20 to-transparent ring-1 ring-[#C5A059]/40' : 'bg-slate-50'}`}
                      data-testid={`rank-row-${r.position}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-sm ${r.position <= 3 ? 'bg-[#C5A059] text-white' : 'bg-slate-200 text-slate-600'}`}>
                        {podium || `#${r.position}`}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">
                          {r.is_me ? `⭐ ${r.name} (voce)` : r.name}
                          {r.type === 'master' && <span className="ml-1.5 text-[9px] font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">MASTER</span>}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-emerald-600">{r.sales_count} {r.sales_count === 1 ? 'venda' : 'vendas'}</p>
                        <p className="text-[10px] text-slate-400">R$ {r.total_amount.toFixed(2).replace('.', ',')}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ══════ TABELA DE GANHOS — Afiliado Padrao ══════ */}
        {!isMaster && (
          <Card className="border-[#C5A059]/20 shadow-sm overflow-hidden" data-testid="earnings-table-card">
            <div className="bg-gradient-to-r from-[#C5A059]/10 to-transparent px-4 sm:px-5 py-3 border-b border-[#C5A059]/10">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#C5A059]" />
                <h3 className="font-bold text-slate-900 text-sm">Tabela de Ganhos por Implantacao</h3>
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">Comissao única no primeiro pagamento do cliente</p>
            </div>
            <CardContent className="p-4 sm:p-5">
              <div className="space-y-2.5">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div><p className="text-sm font-semibold text-slate-800">Plano Básico</p><p className="text-[10px] text-slate-500">R$ 29,90/mes</p></div>
                  <span className="text-lg font-extrabold text-emerald-600">R$ 50,00</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div><p className="text-sm font-semibold text-slate-800">Plano Pro</p><p className="text-[10px] text-slate-500">R$ 39,90/mes</p></div>
                  <span className="text-lg font-extrabold text-emerald-600">R$ 60,00</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div><p className="text-sm font-semibold text-slate-800">Plano Empresarial</p><p className="text-[10px] text-slate-500">R$ 59,90/mes</p></div>
                  <span className="text-lg font-extrabold text-emerald-600">R$ 100,00</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div><p className="text-sm font-semibold text-slate-800">Plano Elite</p><p className="text-[10px] text-slate-500">R$ 99,90/mes</p></div>
                  <span className="text-lg font-extrabold text-emerald-600">R$ 150,00</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#C5A059]/5 rounded-xl border border-[#C5A059]/15">
                  <div><p className="text-sm font-semibold text-slate-800">Plano 5 Estrelas</p><p className="text-[10px] text-slate-500">R$ 149,90/mes</p></div>
                  <span className="text-lg font-extrabold text-[#C5A059]">R$ 250,00</span>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-3 text-center">Valores creditados após confirmacao do primeiro pagamento do cliente</p>
            </CardContent>
          </Card>
        )}

        {/* ══════ GANHOS DE LIDERANCA — Apenas Master ══════ */}
        {isMaster && (
          <Card className="border-[#C5A059]/20 shadow-sm overflow-hidden" data-testid="master-earnings-card">
            <div className="bg-gradient-to-r from-[#C5A059]/10 to-transparent px-4 sm:px-5 py-3 border-b border-[#C5A059]/10">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-[#C5A059]" />
                <h3 className="font-bold text-slate-900 text-sm">Quanto você recebe por vender cada plano</h3>
              </div>
            </div>
            <CardContent className="p-4 sm:p-5">
              <div className="space-y-2.5">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Vendas Proprias</p>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <p className="text-sm font-semibold text-slate-800">Básico</p>
                  <span className="text-base font-extrabold text-emerald-600">R$ 50,00</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <p className="text-sm font-semibold text-slate-800">Pro</p>
                  <span className="text-base font-extrabold text-emerald-600">R$ 60,00</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <p className="text-sm font-semibold text-slate-800">Empresarial</p>
                  <span className="text-base font-extrabold text-emerald-600">R$ 100,00</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <p className="text-sm font-semibold text-slate-800">Elite</p>
                  <span className="text-base font-extrabold text-emerald-600">R$ 150,00</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#C5A059]/5 rounded-xl border border-[#C5A059]/15">
                  <p className="text-sm font-semibold text-slate-800">5 Estrelas</p>
                  <span className="text-base font-extrabold text-[#C5A059]">R$ 250,00</span>
                </div>
                <div className="border-t border-slate-200 pt-3 mt-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Bonus de Equipe</p>
                  <p className="text-[10px] text-blue-600 mb-2">Overriding por venda do afilhado — conforme o plano vendido pela sua rede</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-100">
                      <p className="text-sm font-semibold text-blue-800">Básico ou Pro</p>
                      <span className="text-base font-extrabold text-blue-700">R$ 50,00</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-100">
                      <p className="text-sm font-semibold text-blue-800">Empresarial</p>
                      <span className="text-base font-extrabold text-blue-700">R$ 70,00</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-100">
                      <p className="text-sm font-semibold text-blue-800">Elite ou 5 Estrelas</p>
                      <span className="text-base font-extrabold text-blue-700">R$ 100,00</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ══════ TREINAMENTO DO AFILIADO — disponível para todos ══════ */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-[#0A2647] via-[#0D3158] to-[#0A2647] text-white" data-testid="training-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-[#C5A059]" />
              <CardTitle className="text-base text-white">Central de Treinamento</CardTitle>
            </div>
            <CardDescription className="text-slate-300 text-xs">
              Baixe os materiais de capacitação e se torne um afiliado de alta performance.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href="/materiais/treinamento-afiliado.pdf"
                download="FaloComODono - Guia do Afiliado.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#C5A059]/50 transition-all active:scale-[0.98]"
                data-testid="download-training-guide"
              >
                <div className="w-10 h-10 rounded-lg bg-[#C5A059]/20 flex items-center justify-center shrink-0 group-hover:bg-[#C5A059]/30 transition">
                  <BookOpen className="w-5 h-5 text-[#C5A059]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white mb-0.5">Guia Completo do Afiliado</p>
                  <p className="text-[11px] text-slate-300 leading-snug">
                    Treinamento aprofundado sobre a ferramenta, argumentos de venda e objeções mais comuns.
                  </p>
                  <p className="text-[10px] text-[#C5A059] mt-1.5 flex items-center gap-1">
                    <Download className="w-3 h-3" /> Baixar PDF
                  </p>
                </div>
              </a>
              <a
                href="/materiais/tutorial-afiliado.pdf"
                download="FaloComODono - Tutorial do Afiliado.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#C5A059]/50 transition-all active:scale-[0.98]"
                data-testid="download-tutorial"
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-400/20 flex items-center justify-center shrink-0 group-hover:bg-emerald-400/30 transition">
                  <FileText className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white mb-0.5">Tutorial Passo a Passo</p>
                  <p className="text-[11px] text-slate-300 leading-snug">
                    Guia rápido para você começar a vender e cadastrar seus primeiros clientes hoje mesmo.
                  </p>
                  <p className="text-[10px] text-emerald-400 mt-1.5 flex items-center gap-1">
                    <Download className="w-3 h-3" /> Baixar PDF
                  </p>
                </div>
              </a>
            </div>
            <p className="text-[10px] text-slate-400 mt-3 leading-snug">
              💡 Dica: leia primeiro o <strong className="text-white">Tutorial</strong> para ativar sua conta e depois aprofunde no <strong className="text-white">Guia Completo</strong>.
            </p>
          </CardContent>
        </Card>

        {/* ══════ MINHA REDE — Apenas Master ══════ */}
        {isMaster && team && team.length > 0 && (
          <Card className="border-0 shadow-sm" data-testid="master-team-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#0A2647]" />
                  <CardTitle className="text-base">Minha Rede</CardTitle>
                </div>
                <span className="text-xs text-slate-400">{team_summary?.total_members || 0} afiliado(s)</span>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center p-3 bg-[#0A2647]/5 rounded-xl">
                  <p className="text-lg font-bold text-[#0A2647]">{team_summary?.total_members || 0}</p>
                  <p className="text-[10px] text-slate-500">Afiliados</p>
                </div>
                <div className="text-center p-3 bg-emerald-50 rounded-xl">
                  <p className="text-lg font-bold text-emerald-600">{team_summary?.total_implantations || 0}</p>
                  <p className="text-[10px] text-slate-500">Implantacoes</p>
                </div>
                <div className="text-center p-3 bg-[#C5A059]/10 rounded-xl">
                  <p className="text-lg font-bold text-[#C5A059]">R$ {(team_summary?.total_bonus_earned || 0).toFixed(2)}</p>
                  <p className="text-[10px] text-slate-500">Bonus Gerado</p>
                </div>
              </div>
              {/* Team list */}
              <div className="space-y-2">
                {team.map(member => (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl gap-3" data-testid={`team-member-${member.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-slate-900 truncate">{member.name}</p>
                      <p className="text-[10px] text-slate-400 truncate">{member.email}</p>
                    </div>
                    <div className="flex items-center gap-4 shrink-0 text-center">
                      <div>
                        <p className="text-sm font-bold text-[#0A2647]">{member.active_companies}<span className="text-slate-400 font-normal">/{member.total_companies}</span></p>
                        <p className="text-[9px] text-slate-400">Ativos / Total</p>
                      </div>
                      <div className="w-px h-8 bg-slate-200" />
                      <div>
                        <p className="text-sm font-bold text-[#C5A059]">R$ {member.bonus_total.toFixed(2)}</p>
                        <p className="text-[9px] text-slate-400">
                          {member.bonus_paid > 0 && <span className="text-emerald-600">Pago: R$ {member.bonus_paid.toFixed(2)}</span>}
                          {member.bonus_pending > 0 && <span className={member.bonus_paid > 0 ? ' ml-1' : ''}>{member.bonus_paid > 0 ? '| ' : ''}Pendente: R$ {member.bonus_pending.toFixed(2)}</span>}
                          {member.bonus_total === 0 && 'Sem bonus'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4 text-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#0A2647] mx-auto mb-1.5 sm:mb-2" />
              <p className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="aff-referral-count">{referral_count}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Indicados</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4 text-center">
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-[#C5A059] mx-auto mb-1.5 sm:mb-2" />
              <p className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="aff-total-gross">R$ {summary.total_gross.toFixed(2)}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Comissao</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4 text-center">
              <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500 mx-auto mb-1.5 sm:mb-2" />
              <p className="text-xl sm:text-2xl font-bold text-emerald-600" data-testid="aff-total-paid">R$ {summary.total_paid.toFixed(2)}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Pago</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4 text-center">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 mx-auto mb-1.5 sm:mb-2" />
              <p className="text-xl sm:text-2xl font-bold text-amber-600" data-testid="aff-total-scheduled">R$ {summary.total_scheduled.toFixed(2)}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Agendado</p>
            </CardContent>
          </Card>
        </div>

        {/* Click Analytics */}
        <ClickAnalyticsCard
          endpoint="/affiliates/me/clicks"
          authHeader={{ Authorization: `Bearer ${getToken()}` }}
        />

        {/* Implantar cliente (wizard de venda) */}
        <div id="implantar-cliente" data-testid="affiliate-implant-section">
          <PartnerImplantClientsTab
            sellerType="affiliate"
            endpoints={{ submit: '/affiliates/implant-client', list: '/affiliates/implanted-clients' }}
            headers={{ Authorization: `Bearer ${getToken()}` }}
          />
        </div>

        {/* Commission Breakdown */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Extrato de Comissoes</CardTitle>
            <CardDescription>Detalhamento de cada comissao gerada</CardDescription>
          </CardHeader>
          <CardContent>
            {commissions.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">
                Nenhuma comissao gerada ainda. Compartilhe seu link para começar a ganhar!
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-slate-500 text-xs">
                      <th className="text-left py-2 px-3">Empresa</th>
                      <th className="text-center py-2 px-3">Comissao</th>
                      <th className="text-center py-2 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map(c => (
                      <tr key={c.id} className="border-b border-slate-100">
                        <td className="py-3 px-3">
                          <p className="font-medium text-slate-900">{c.company_name}</p>
                          <p className="text-xs text-slate-400">{c.plan_name}</p>
                        </td>
                        <td className="text-center py-3 px-3 font-bold text-emerald-600">R$ {(c.installment_1?.amount || c.gross_amount || 0).toFixed(2)}</td>
                        <td className="text-center py-3 px-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            c.installment_1?.status === 'paid' ? 'bg-emerald-100 text-emerald-700'
                              : c.installment_1?.status === 'awaiting_client_payment' ? 'bg-slate-100 text-slate-600'
                              : 'bg-amber-100 text-amber-700'
                          }`} data-testid={`aff-commission-status-${c.id}`}>
                            {c.installment_1?.status === 'paid' ? 'Pago'
                              : c.installment_1?.status === 'awaiting_client_payment' ? 'Aguardando pagamento do cliente'
                              : 'A pagar'}
                          </span>
                          {c.installment_1?.status === 'paid' && c.installment_1?.receipt_url && (
                            <a
                              href={c.installment_1.receipt_url.startsWith('http') ? c.installment_1.receipt_url : `${process.env.REACT_APP_BACKEND_URL}${c.installment_1.receipt_url}`}
                              target="_blank" rel="noopener noreferrer"
                              className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 hover:text-emerald-800 underline"
                              data-testid={`aff-receipt-${c.id}-1`}
                            >
                              <Download className="w-3 h-3" />Comprovante
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Referrals List */}
        {referrals.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Empresas Indicadas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {referrals.map((r) => (
                  <div key={r.company_id || r.name} className="flex items-center justify-between px-3 py-2.5 bg-slate-50 rounded-lg gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 text-sm flex items-center gap-1.5">
                        {r.name}
                        {r.is_vip && <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ring-1 ${
                          r.subscription_status === 'vitrine' ? 'bg-[#C5A059]/10 text-[#C5A059] ring-[#C5A059]/40' : 'bg-amber-100 text-amber-700 ring-amber-300'
                        }`}>{r.subscription_status === 'vitrine' ? 'Vitrine' : 'VIP'}</span>}
                      </p>
                      <p className="text-xs text-slate-400">{r.created_at?.slice(0, 10)}{r.vip_expires_at ? ` · VIP ate ${r.vip_expires_at.slice(0, 10)}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        r.subscription_status === 'active' ? 'bg-emerald-100 text-emerald-700' : r.subscription_status === 'vitrine' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {r.subscription_status === 'active' ? 'Ativo' : r.subscription_status === 'vitrine' ? 'Vitrine' : 'Pendente'}
                      </span>
                      {!r.is_vip && !r.vip_request_pending && (
                        vitrineGranting === r.company_id ? (
                          <div className="flex items-center gap-1.5" data-testid={`vitrine-form-${r.company_id}`}>
                            <select
                              value={vitrineMonths}
                              onChange={(e) => setVitrineMonths(Number(e.target.value))}
                              className="text-xs border border-[#C5A059] rounded-lg px-2 py-1 bg-white"
                              data-testid={`vitrine-months-${r.company_id}`}
                            >
                              <option value={3}>3 meses</option>
                              <option value={6}>6 meses</option>
                              <option value={12}>12 meses</option>
                            </select>
                            <button
                              onClick={() => grantVitrine(r.company_id)}
                              className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-[#C5A059] text-white hover:bg-[#b8933f] transition-colors"
                              data-testid={`vitrine-confirm-${r.company_id}`}
                            >
                              Conceder
                            </button>
                            <button
                              onClick={() => setVitrineGranting(null)}
                              className="text-xs px-2 py-1 rounded-lg border border-slate-200 text-slate-400 hover:bg-slate-50"
                            >
                              X
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setVitrineGranting(r.company_id); setVitrineMonths(3); }}
                            className="text-xs font-medium px-2.5 py-1 rounded-lg bg-[#C5A059]/10 text-[#C5A059] hover:bg-[#C5A059]/20 border border-[#C5A059]/30 transition-colors"
                            data-testid={`vitrine-btn-${r.company_id}`}
                          >
                            Conceder Vitrine
                          </button>
                        )
                      )}
                      {r.vip_request_pending && !r.is_vip && (
                        <span className="text-xs font-medium px-2.5 py-1 rounded-lg bg-yellow-50 text-yellow-700 border border-yellow-200" data-testid={`vip-pending-${r.company_id}`}>
                          VIP Solicitado
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
      <HelpButton ambiente="afiliado" tela="painel" />
    </div>
  );
};

export default AffiliateDashboard;
